import { useState, useEffect, useContext } from "react";
import { Row, Col, Spinner, Alert } from "react-bootstrap";
import PeriodFilter from "../PeriodFilter";
import StatCard from "../StatCard";
import CustomPeriodModal from "../CustomPeriodModal";
import CarrerasChart from "../CarrerasChart";
import EstadosEstudiantesChart from "../EstadosEstudiantesChart";
import TopEstudiantesTable from "../TopEstudiantesTable";
import { PeriodContext } from "../../../pages/jefatura/Dashboard";
import { obtenerEstadisticasEstudiantes, obtenerAtencionesPorCarrera } from "../../../services/apis_jefatura";

function EstudiantesTab() {
  // Usar el contexto del Dashboard para el período
  const { selectedPeriod, setSelectedPeriod, customDates, setCustomDates } = useContext(PeriodContext);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para los datos
  const [estudiantesUnicos, setEstudiantesUnicos] = useState(0);
  const [estudiantesConFlag, setEstudiantesConFlag] = useState(0);
  const [carrerasData, setCarrerasData] = useState(null);
  const [estadosData, setEstadosData] = useState(null);
  const [topEstudiantes, setTopEstudiantes] = useState([]);
  const [estudiantesFlag, setEstudiantesFlag] = useState([]);

  const [loadingCarreras, setLoadingCarreras] = useState(false);

  // Función para cargar datos de estudiantes
  const cargarEstadisticas = async (params) => {
    setLoading(true);
    setError(null);

    try {
      const data = await obtenerEstadisticasEstudiantes(params);

      setEstudiantesUnicos(data.estudiantes_unicos || 0);
      setEstudiantesConFlag(data.estudiantes_con_flag || 0);
      setEstadosData(data.estados || null);
      setTopEstudiantes(data.top_estudiantes || []);
      setEstudiantesFlag(data.estudiantes_flag || []);
    } catch (err) {
      console.error("Error cargando estadísticas de estudiantes:", err);
      setError("No se pudieron cargar las estadísticas. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar datos de carreras
  const cargarCarreras = async (params) => {
    setLoadingCarreras(true);

    try {
      const data = await obtenerAtencionesPorCarrera(params);
      setCarrerasData(data);
    } catch (err) {
      console.error("Error cargando carreras:", err);
      setCarrerasData(null);
    } finally {
      setLoadingCarreras(false);
    }
  };

  // Cargar datos al montar el componente y cuando cambie el período
  useEffect(() => {
    const params = {};

    if (selectedPeriod !== "custom") {
      params.period = selectedPeriod;
    } else if (customDates) {
      params.startDate = customDates.startDate;
      params.endDate = customDates.endDate;
    }

    // Solo cargar si tenemos un período válido
    if (params.period || (params.startDate && params.endDate)) {
      cargarEstadisticas(params);
      cargarCarreras(params);
    }
  }, [selectedPeriod, customDates]);

  const handlePeriodChange = (period) => {
    if (period === "custom") {
      setShowCustomModal(true);
    } else {
      setSelectedPeriod(period);
      setCustomDates(null);
    }
  };

  const handleCustomPeriodApply = (dates) => {
    setSelectedPeriod("custom");
    setCustomDates(dates);
  };

  const handleCustomModalClose = () => {
    setShowCustomModal(false);
    if (selectedPeriod === "custom" && !customDates) {
      // Si se cancela y no hay fechas guardadas, volver al último mes
      setSelectedPeriod("last_month");
    }
  };

  return (
    <div>
      <PeriodFilter
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
      />

      {/* Mostrar error si existe */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Mostrar spinner mientras carga */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
          <p className="mt-3 text-muted">Cargando estadísticas...</p>
        </div>
      )}

      {/* Cards de resumen */}
      {!loading && (
        <Row className="g-4 mb-4">
          <Col xs={12} md={6}>
            <StatCard
              title="Estudiantes Únicos"
              value={estudiantesUnicos.toString()}
              subtitle="Atendidos en el período"
              icon="bi bi-people"
              iconColor="blue"
              valueColor="blue"
            />
          </Col>

          <Col xs={12} md={6}>
            <StatCard
              title="Estudiantes con Flag"
              value={estudiantesConFlag.toString()}
              subtitle="Requieren atención especial"
              icon="bi bi-flag"
              iconColor="yellow"
              valueColor="yellow"
            />
          </Col>
        </Row>
      )}

      {/* Gráficos */}
      {!loading && (
        <Row className="g-4 mb-4">
          <Col xs={12} lg={6}>
            {loadingCarreras ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando carreras...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando carreras...</p>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm">
                <div className="card-body">
                  <CarrerasChart
                    data={carrerasData}
                    period={selectedPeriod}
                  />
                </div>
              </div>
            )}
          </Col>

          <Col xs={12} lg={6}>
            <div className="card shadow-sm">
              <div className="card-body">
                <EstadosEstudiantesChart data={estadosData} />
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Tablas */}
      {!loading && (
        <Row className="g-4">
          <Col xs={12}>
            <TopEstudiantesTable
              estudiantes={topEstudiantes}
              title="Top Estudiantes con Más Atenciones"
            />
          </Col>

          <Col xs={12}>
            <TopEstudiantesTable
              estudiantes={estudiantesFlag}
              title="Últimos Estudiantes con Flag Activada"
            />
          </Col>
        </Row>
      )}

      <CustomPeriodModal
        show={showCustomModal}
        onHide={handleCustomModalClose}
        onApply={handleCustomPeriodApply}
      />
    </div>
  );
}

export default EstudiantesTab;
